import { logger } from '@/lib/logger';

// Google OAuth Service for Calendar + Gmail
const GOOGLE_CLIENT_ID = '952989944552-tsluak910l8lppd4si363kpo6pta0kis.apps.googleusercontent.com';
const SCOPES = [
    'https://www.googleapis.com/auth/calendar.events',
    'https://www.googleapis.com/auth/gmail.readonly',
    'https://www.googleapis.com/auth/gmail.send',
    'https://www.googleapis.com/auth/gmail.compose',
    'https://www.googleapis.com/auth/drive.readonly'
].join(' ');

// Token storage keys
const TOKEN_KEY = 'google_oauth_token';
const USER_KEY = 'google_user_info';

// Initialize Google Identity Services
let tokenClient = null;
let gsiLoaded = false;

export const loadGoogleScript = () => {
    return new Promise((resolve, reject) => {
        if (gsiLoaded) {
            resolve();
            return;
        }

        // Check if already loaded
        if (window.google?.accounts) {
            gsiLoaded = true;
            resolve();
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = () => {
            gsiLoaded = true;
            resolve();
        };
        script.onerror = reject;
        document.head.appendChild(script);
    });
};

export const initTokenClient = (callback) => {
    if (!window.google?.accounts?.oauth2) {
        logger.error('Google Identity Services not loaded');
        return null;
    }

    tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: SCOPES,
        callback: (response) => {
            if (response.error) {
                logger.error('OAuth error:', response);
                callback(null, response.error);
                return;
            }
            // Store token
            const tokenData = {
                access_token: response.access_token,
                expires_at: Date.now() + (response.expires_in * 1000)
            };
            sessionStorage.setItem(TOKEN_KEY, JSON.stringify(tokenData));
            callback(tokenData, null);
        }
    });

    return tokenClient;
};

export const requestAccessToken = () => {
    if (tokenClient) {
        tokenClient.requestAccessToken();
    }
};

export const getStoredToken = () => {
    const stored = sessionStorage.getItem(TOKEN_KEY);
    if (!stored) return null;

    const tokenData = JSON.parse(stored);
    // Check if expired (with 5 min buffer)
    if (tokenData.expires_at < Date.now() + 300000) {
        sessionStorage.removeItem(TOKEN_KEY);
        return null;
    }
    return tokenData;
};

export const isConnected = () => {
    return getStoredToken() !== null;
};

export const disconnectGoogle = () => {
    const token = getStoredToken();
    if (token?.access_token) {
        // Revoke token
        window.google?.accounts?.oauth2?.revoke(token.access_token, () => {
            logger.debug('Token revoked');
        });
    }
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);
};

export const fetchGoogleUserInfo = async () => {
    const token = getStoredToken();
    if (!token) return null;

    try {
        const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
            headers: {
                Authorization: `Bearer ${token.access_token}`
            }
        });
        if (!response.ok) throw new Error('Failed to fetch user info');
        const userInfo = await response.json();
        sessionStorage.setItem(USER_KEY, JSON.stringify(userInfo));
        return userInfo;
    } catch (error) {
        logger.error('Error fetching user info:', error);
        return null;
    }
};

export const getStoredUserInfo = () => {
    const stored = sessionStorage.getItem(USER_KEY);
    return stored ? JSON.parse(stored) : null;
};
